import { useEffect, useRef, useState, useCallback } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { useStackRealtime } from "./useStackRealtime";

// ── Types (mirror backend pod-status.types.ts) ────────────────────────────────

export interface PodStatusSnapshot {
  podName: string;
  serviceName: string;
  /** System alias — from rusha.io/system label. Undefined for shared project-level services. */
  systemAlias?: string;
  phase: string;
  ready: boolean;
  restarts: number;
  startedAt: string | null;
  cpu?: string;
  memory?: string;
}

export interface PodMonitorSnapshot {
  namespace: string;
  /** Project alias derived from the namespace (e.g., "customer-portal" from "acme-corp-customer-portal"). */
  projectAlias?: string;
  timestamp: string;
  pods: PodStatusSnapshot[];
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UsePodMonitorResult {
  snapshot: PodMonitorSnapshot | null;
  loading: boolean;
  error: string | null;
  connected: boolean;
  refresh: () => void;
}

/** Polling interval (ms) when WebSocket is disconnected */
const POLL_INTERVAL_MS = 10_000;

/**
 * Real-time pod monitoring hook.
 *
 * 1. On mount: fetches a one-shot snapshot from `GET /api/v1/applications/:alias/pods`
 * 2. Opens a Socket.io connection to `/pods` namespace
 * 3. Subscribes to the alias — backend streams PodMonitorSnapshot on each K8s event
 * 4. Falls back to REST polling every 10s when WebSocket is disconnected
 * 5. On unmount: unsubscribes and disconnects the socket
 */
export function usePodMonitor(alias: string | undefined): UsePodMonitorResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [snapshot, setSnapshot] = useState<PodMonitorSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  const fetchSnapshot = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_PODS.replace(":id", alias)}`;
      const res = await axios.get<PodMonitorSnapshot>(url, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setSnapshot(res.data);
    } catch {
      setError("Failed to load pod status.");
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    if (!alias) return;

    // Initial REST snapshot
    void fetchSnapshot();

    // Open WebSocket — connect to the /pods namespace
    const socket = io(`${apiBaseUrls.BACKEND}/pods`, {
      auth: { token: token() },
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 20,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribe", { alias });
      // Stop polling while connected
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    });

    socket.on("pods", (data: PodMonitorSnapshot) => {
      setSnapshot(data);
      setError(null);
    });

    socket.on("error", (data: { message: string }) => {
      setError(data.message);
    });

    socket.on("disconnect", () => {
      setConnected(false);
      // Start polling fallback when disconnected
      if (!pollRef.current) {
        void fetchSnapshot();
        pollRef.current = setInterval(
          () => void fetchSnapshot(),
          POLL_INTERVAL_MS,
        );
      }
    });

    return () => {
      socket.emit("unsubscribe", { alias });
      socket.disconnect();
      socketRef.current = null;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [alias, fetchSnapshot]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      snapshot: ctx.podSnapshot,
      loading: ctx.podLoading,
      error: ctx.podError,
      connected: ctx.podsConnected,
      refresh: ctx.refetchPods,
    };
  }

  return {
    snapshot,
    loading,
    error,
    connected,
    refresh: fetchSnapshot,
  };
}
